import type { MouseEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collectEventNavItems, isPlainLeftClick, type EventNavState } from '../utils/eventNav';

interface Props {
  eventId: string;
  title: string;
  className?: string;
  children: ReactNode;
}

/**
 * A link to an event page that carries its surrounding list (the nearest
 * `data-event-nav-scope` ancestor) along, so the event page can offer
 * Previous/Next through that same list.
 */
export function EventLink({ eventId, title, className, children }: Props) {
  const navigate = useNavigate();
  const to = `/events/${eventId}`;

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!isPlainLeftClick(e)) return;
    const items = collectEventNavItems(e.currentTarget);
    if (items.length < 2) return; // nothing to step through — plain Link navigation
    e.preventDefault();
    const state: EventNavState = { eventNav: { items } };
    navigate(to, { state });
  }

  return (
    <Link
      to={to}
      className={className}
      onClick={handleClick}
      data-event-nav-id={eventId}
      data-event-nav-title={title}
    >
      {children}
    </Link>
  );
}
